import React, { useState } from 'react';
import { motion } from 'framer-motion';

const PortfolioPage = () => {
  const categories = [
    {
      title: "Bridal Collection",
      description: "Elegant wedding gowns and bridal wear",
      image: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      link: "/portfolio/bridal"
    },
    {
      title: "Ready to Wear",
      description: "Contemporary fashion for everyday elegance",
      image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      link: "/portfolio/ready-to-wear"
    },
    {
      title: "Sustainable Fashion",
      description: "Eco-friendly and ethical fashion designs",
      image: "https://images.unsplash.com/photo-1581655353564-df123a1eb820?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      link: "/portfolio/sustainable"
    },
    {
      title: "Custom Design",
      description: "Bespoke fashion pieces and alterations",
      image: "https://images.unsplash.com/photo-1558769132-cb1aea458c5e?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      link: "/portfolio/custom"
    }
  ];

  const projects = [
    {
      title: "Haute Couture Collection 2024",
      category: "Custom Design",
      image: "https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      client: "Paris Fashion Week"
    },
    {
      title: "Spring Essentials",
      category: "Ready to Wear",
      image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      client: "Retail Collection"
    },
    {
      title: "Ethereal Bridal Collection",
      category: "Bridal Collection",
      image: "https://images.unsplash.com/photo-1595777457583-95e059d581b8?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      client: "Luxury Bridal Boutique"
    },
    {
      title: "Eco-Conscious Line",
      category: "Sustainable Fashion",
      image: "https://images.unsplash.com/photo-1581375074612-d1fd0e661aeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      client: "Sustainable Fashion Initiative"
    },
    {
      title: "Modern Minimalist Bridal",
      category: "Bridal Collection",
      image: "https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      client: "Urban Bride Studio"
    },
    {
      title: "Eco Luxe Eveningwear",
      category: "Sustainable Fashion",
      image: "https://images.unsplash.com/photo-1512436991641-6745cdb1723f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      client: "Green Gala 2023"
    },
    {
      title: "Classic Tailored Suits",
      category: "Ready to Wear",
      image: "https://images.unsplash.com/photo-1512436991641-6745cdb1723f?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      client: "Executive Menswear"
    },
    {
      title: "Bespoke Evening Gowns",
      category: "Custom Design",
      image: "https://images.unsplash.com/photo-1503342217505-b0a15ec3261c?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      client: "Red Carpet Events"
    },
    {
      title: "Bohemian Summer Dresses",
      category: "Ready to Wear",
      image: "https://images.unsplash.com/photo-1517841905240-472988babdf9?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      client: "Summer Collection 2023"
    },
    {
      title: "Urban Streetwear Capsule",
      category: "Ready to Wear",
      image: "https://images.unsplash.com/photo-1509319117193-57bab727e09d?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80",
      client: "City Style Launch"
    }
  ];

  const [selectedCategory, setSelectedCategory] = useState(categories[0].title);

  const filteredProjects = projects.filter(
    (project) => project.category === selectedCategory
  );

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const staggerChildren = {
    animate: {
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-20"
    >
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1515372039744-b8f02a3ae446?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        <motion.div 
          className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
          variants={staggerChildren}
          initial="initial"
          animate="animate"
        >
          <motion.h1 
            variants={fadeInUp}
            className="text-5xl md:text-7xl font-bold mb-6"
          >
            <span className="block">Our Portfolio</span>
            <span className="block">Showcase</span>
          </motion.h1>
          <motion.p 
            variants={fadeInUp}
            className="text-xl text-gray-300 max-w-3xl mx-auto"
          >
            Explore our portfolio of bespoke fashion designs and innovative collections.
          </motion.p>
        </motion.div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Categories */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            {categories.map((category, index) => (
              <button
                key={index}
                className={`px-6 py-2 rounded-full transition-colors font-semibold ${
                  selectedCategory === category.title
                    ? 'bg-emerald-400 text-black shadow-lg'
                    : 'border border-emerald-400 text-emerald-400 hover:bg-emerald-400 hover:text-black'
                }`}
                onClick={() => setSelectedCategory(category.title)}
              >
                {category.title}
              </button>
            ))}
          </div>

          {/* Projects Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.length === 0 ? (
              <div className="col-span-full text-center text-gray-400 py-16 text-xl">No projects found for this category.</div>
            ) : (
              filteredProjects.map((project, index) => (
                <div key={index} className="group relative overflow-hidden rounded-lg">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-[400px] object-cover transform group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 p-8">
                      <p className="text-emerald-400 text-sm mb-2">{project.category}</p>
                      <h3 className="text-2xl font-bold mb-2">{project.title}</h3>
                      <p className="text-gray-300">{project.client}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Awards Section */}
      <section className="py-24 bg-black/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-16">Recognition & Awards</h2>
            <div className="grid md:grid-cols-3 gap-12">
              {[
                {
                  year: "2024",
                  award: "Fashion Designer of the Year",
                  organization: "International Fashion Awards"
                },
                {
                  year: "2023",
                  award: "Best Sustainable Collection",
                  organization: "Sustainable Fashion Council"
                },
                {
                  year: "2023",
                  award: "Innovation in Textile Design",
                  organization: "Global Fashion Summit"
                }
              ].map((award, index) => (
                <div key={index} className="p-8 border border-gray-800 rounded-lg">
                  <p className="text-emerald-400 text-lg mb-2">{award.year}</p>
                  <h3 className="text-xl font-bold mb-2">{award.award}</h3>
                  <p className="text-gray-400">{award.organization}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default PortfolioPage;